import { OrderTransactionItem } from '@reg/shared/model/order-transaction-item.model';
import get from 'lodash-es/get';

export enum OrderTransactionType {
    REFUND = 'refund',
    CHARGE = 'charge',
}

/**
 * Model to hold OrderTransaction details for an Order
 */
export class OrderTransaction {
    public id: string;
    public transactionType: string;
    public description: string;
    public amount: number;
    public fee: number;
    public status: string;
    public isTest: boolean;
    public userId: string;
    public currency: string;
    public dateTime: Date;
    public errorCode: string;
    public errorMessage: string;
    public responseId: string;
    public orderId: string;
    public transactionItems: OrderTransactionItem[] = [];
    public canRefund: boolean;

    public get isStatusSucceeded(): boolean {
        return this.status === 'succeeded';
    }

    /**
     * Serialize from to json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'orderTransaction';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.transactionType = this.transactionType;
        json.data.attributes.responseId = this.responseId;
        json.data.attributes.description = this.description;
        json.data.attributes.userId = this.userId;
        json.data.attributes.amount = this.amount;
        json.data.attributes.fee = this.fee;
        json.data.attributes.status = this.status;
        json.data.attributes.isTest = this.isTest;
        json.data.attributes.currency = this.currency;
        json.data.attributes.errorCode = this.errorCode;
        json.data.attributes.errorMessage = this.errorMessage;
        json.data.attributes.orderId = this.orderId;
        if (this.dateTime) {
            json.data.attributes.dateTimeUtc = this.dateTime.toISOString();
        }
        return JSON.stringify(json);
    }

    /**
     * Deserialize from a json api format
     */
    public deserialize(json: any, included?: any[]): OrderTransaction {
        const data: any = json.data || json;
        const attrs: any = data.attributes;
        included = included || [];

        this.id = data.id;
        this.userId = attrs.userId;
        this.status = attrs.status;
        this.transactionType = attrs.transactionType;
        this.description = attrs.description;
        this.responseId = attrs.responseId;
        this.amount = parseFloat(attrs.amount);
        this.fee = attrs.fee || attrs.fee === 0 ? parseFloat(attrs.fee) : null;
        this.isTest = attrs.isTest;
        this.currency = attrs.currency;
        this.errorCode = attrs.errorCode;
        this.errorMessage = attrs.errorMessage;
        this.orderId = attrs.orderId;

        if (attrs.dateTime) {
            this.dateTime = new Date(attrs.dateTime);
        }

        // Populate the order transaction items
        const transactionItemList: any[] = get(data, 'relationships.orderTransactionItems.data') || [];
        const childItems: OrderTransactionItem[] = [];

        transactionItemList.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                const ot: OrderTransactionItem = (new OrderTransactionItem()).deserialize(item);

                if (ot.parentId) {
                    childItems.push(ot);
                } else {
                    this.transactionItems.push(ot);
                }
            }
        });

        // Set the order transaction item children
        this.transactionItems.forEach((ot: OrderTransactionItem) => {
            ot.children = childItems.filter((x: OrderTransactionItem) => x.parentId === ot.id);
        });

        // Check if we have at least one transaction item that's refundable
        const canRefundAtLeastOneTransaction: boolean =
            !!this.transactionItems.find((ti: OrderTransactionItem) => ti.canRefund && ti.onlineAmountPaid > 0);

        this.canRefund = canRefundAtLeastOneTransaction &&
            this.isStatusSucceeded &&
            this.amount > 0 &&
            this.transactionType === OrderTransactionType.CHARGE;

        return this;
    }
}
